import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ChartOfAccountsItem, ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { isPostingAccount } from "../../../../store/selectors/metaDataSelectors.ts";
import { DimensionNames } from "../../../../utilities/DimensionNames.ts";
import type { StatementAccountLedger } from "./statementRowsArea.ts";

export type StatementChartLedger = "All" | "Gl" | "Memo";

export interface StatementChartAccount extends ChartOfAccountsItem {
  ledger: StatementAccountLedger;
}

export interface ChartOfAccountsCategoryGroup<T extends ChartOfAccountsItem = ChartOfAccountsItem> {
  category: string;
  caption: string;
  accounts: T[];
}

const getAccountingBookFilter = (
  conditions: Pick<ConditionField, "config">[]
): ((book: string | null) => boolean) | undefined => {
  const condition = conditions.find((item) => item.config.filter.dimensionName === DimensionNames.AccountingBook);
  if (!condition || condition.config.filter.values.length === 0) {
    return undefined;
  }
  const { values, equalityType } = condition.config.filter;
  if (equalityType === undefined || equalityType === ConditionEqualityType.Equal) {
    return (book) => values.includes(book);
  }
  if (equalityType === ConditionEqualityType.NotEqual) {
    return (book) => !values.includes(book);
  }
  return undefined;
};

const dedupeByAccountNo = (accounts: ChartOfAccountsItem[]): ChartOfAccountsItem[] => {
  const seenAccountNos = new Set<string>();
  return accounts.filter((account) => {
    if (seenAccountNos.has(account.accountNo)) {
      return false;
    }
    seenAccountNos.add(account.accountNo);
    return true;
  });
};

export const getStatementGlAccounts = (
  gl: ChartOfAccountsItem[],
  multiBook: boolean,
  conditions: Pick<ConditionField, "config">[]
): ChartOfAccountsItem[] => {
  const posting = gl.filter(isPostingAccount);
  if (!multiBook) {
    return posting;
  }
  const isSelectedBook = getAccountingBookFilter(conditions);
  return dedupeByAccountNo(
    isSelectedBook ? posting.filter((account) => isSelectedBook(account.accountingBook)) : posting
  );
};

export const getStatementAccounts = (
  chartOfAccounts: { gl: ChartOfAccountsItem[]; memo: ChartOfAccountsItem[] },
  ledger: StatementChartLedger,
  multiBook: boolean,
  conditions: Pick<ConditionField, "config">[],
  memoAvailable: boolean
): StatementChartAccount[] => {
  const gl =
    ledger === "Memo"
      ? []
      : getStatementGlAccounts(chartOfAccounts.gl, multiBook, conditions).map((account): StatementChartAccount => ({
          ...account,
          ledger: "Gl",
        }));
  const memo =
    ledger === "Gl" || !memoAvailable
      ? []
      : (chartOfAccounts.memo ?? [])
          .filter(isPostingAccount)
          .map((account): StatementChartAccount => ({ ...account, ledger: "Memo" }));
  return [...gl, ...memo];
};

export const searchStatementAccounts = <T extends ChartOfAccountsItem>(accounts: T[], searchText: string): T[] => {
  if (searchText === "") {
    return accounts;
  }
  return accounts.filter(
    (account) =>
      account.accountNo.toLowerCase().indexOf(searchText) > -1 ||
      account.accountName.toLowerCase().indexOf(searchText) > -1 ||
      account.accountSubcategory.toLowerCase().indexOf(searchText) > -1
  );
};

export const groupStatementAccountsByCategory = <T extends ChartOfAccountsItem>(
  accounts: T[]
): ChartOfAccountsCategoryGroup<T>[] => {
  const groups: ChartOfAccountsCategoryGroup<T>[] = [];
  const byCategory = new Map<string, ChartOfAccountsCategoryGroup<T>>();
  accounts.forEach((account) => {
    let group = byCategory.get(account.accountCategory);
    if (!group) {
      group = {
        category: account.accountCategory,
        caption: account.accountCategoryName || account.accountCategory,
        accounts: [],
      };
      byCategory.set(account.accountCategory, group);
      groups.push(group);
    }
    group.accounts.push(account);
  });
  return groups;
};

export const getAccountRowTooltip = (account: ChartOfAccountsItem & { ledger?: StatementAccountLedger }): string => {
  const measure = account.ledger === "Memo" ? "Memo Amount" : "Amount";
  const details = [account.accountSubcategory, account.incomeBalance].filter(Boolean).join(" · ");
  return `${account.accountNo} ${account.accountName}${details ? ` — ${details}` : ""}. Drag into Rows for a ${measure} filtered to this account.`;
};
